import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Info, HelpCircle } from 'lucide-react';
import Header from '../components/layout/Header';
import UrlForm from '../components/analysis/UrlForm';
import UrlGuideOverlay from '../components/analysis/UrlGuideOverlay';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { api } from '../services/api';

export default function AnalyzeUrlPage() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showGuide, setShowGuide] = useState(false);
  const navigate = useNavigate();

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setUrl(text);
      setError('');
    } catch {
      setError('Impossible de lire le presse-papier. Collez le lien manuellement.');
    }
  };

  const handleAnalyze = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.analyzeUrl(url.trim());
      api.saveToHistory(data);
      navigate('/results', { state: { type: 'url', data } });
    } catch {
      setLoading(false);
      setError('Analyse impossible pour le moment. Vérifiez votre connexion puis réessayez.');
    }
  };

  if (loading) return <LoadingSpinner url={url} currentStep={1} />;

  return (
    <div className="page-container">
      <Header showBack backLabel="Vérifier un lien" onBack={() => navigate('/analyze')} />
      <div className="mx-4 mt-6">
        <h1 className="font-bold text-2xl text-text-primary">Vérifier un lien</h1>
        <p className="text-text-muted text-sm mt-1 leading-relaxed">Copiez et collez l'URL suspecte pour une analyse immédiate par nos algorithmes de sécurité.</p>
        <button
          type="button"
          onClick={() => setShowGuide(true)}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-accent-blue active:opacity-70 transition-opacity"
        >
          <HelpCircle className="w-3.5 h-3.5" />
          Comment faire ?
        </button>
      </div>

      <div className="mx-4 mt-4"><UrlForm value={url} onChange={setUrl} onPaste={handlePaste} /></div>
      {error ? <div className="mx-4 mt-4"><ErrorMessage message={error} /></div> : null}

      <div className="mx-4 mt-4 flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100">
        <Info className="w-6 h-6 text-accent-blue flex-shrink-0" />
        <p className="text-text-muted text-sm leading-relaxed">Notre système analyse les métadonnées, les certificats SSL, et les bases de données mondiales de phishing pour protéger vos transactions.</p>
      </div>

      <div className="sticky-action-bar">
        <button type="button" onClick={handleAnalyze} disabled={!url.trim()} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">Analyser ce lien <ArrowRight className="w-5 h-5" /></button>
        <p className="text-center text-text-subtle text-xs mt-2">Analyse confidentielle · Aucune donnée revendue</p>
      </div>

      {showGuide && <UrlGuideOverlay onClose={() => setShowGuide(false)} />}
    </div>
  );
}
